/**
 * Изделия магазина: как выглядят и куда наносится принт.
 *
 * Один источник правды для конструктора, серверной сборки мокапа и каталога.
 * Те же числа лежат в общем пакете с ботом — менять только парой.
 *
 * Все доли считаются от видимой области изделия, не от исходного файла.
 * У футболки файл шире видимой области и обрезается сверху, у сумки и кепки
 * картинки уже обрезаны по краям изделия.
 */

export type Side = 'front' | 'back';
export type GarmentType = 'tshirt' | 'tote' | 'cap';

export interface Garment {
  type: GarmentType;
  title: string;
  /**
   * Название во множественном числе — для вкладок витрины.
   *
   * Отдельным полем, а не правилом «добавить -и»: по-русски такого
   * правила нет. «Футболка» и «Кепка» дают «Футболки» и «Кепки»,
   * а «Шоппер» — «Шоперы», и любое выведенное окончание однажды
   * споткнётся на следующем виде вещи.
   */
  plural: string;
  /** Стороны, которые показывает конструктор. */
  sides: Side[];
  /** Цвета изделия. Пустой список — изделие только в исходном цвете. */
  colors: string[];
  /** Ширина к высоте видимой области. */
  aspect: number;
  /** Подложка в долях высоты видимой области: сдвиг вверх и собственная высота. */
  base: { top: number; height: number };
  /** Зона печати в долях видимой области. */
  zone: { x: number; y: number; width: number; height: number };
}

export const GARMENTS: Record<GarmentType, Garment> = {
  // Файлы 1215×1125, по одному на цвет и сторону. Обрезать нечего:
  // изделие уже вписано в кадр, поэтому подложка совпадает с рамкой.
  tshirt: {
    type: 'tshirt',
    title: 'Футболка',
    plural: 'Футболки',
    sides: ['front', 'back'],
    colors: ['black', 'graphite', 'charcoal', 'navy', 'grey', 'white', 'beige', 'pink'],
    aspect: 1215 / 1125,
    base: { top: 0, height: 1 },
    // Зона печати — та же, что в конструкторе effective-records
    // (design.store.ts, areas.front.box). Своя была уже 33% против 40%,
    // и собранный принт в неё не влезал: снимок жался по бокам, длинная
    // надпись обрывалась. Печатают на одной и той же футболке, значит
    // и зона должна быть одна.
    //
    // NOTE: пара к areas.front.box в design.store.ts. Разойдутся — принт
    // на сайте встанет не так, как уедет в печать.
    zone: { x: 0.3, y: 0.22, width: 0.4, height: 0.46 },
  },

  // Сумка 363×723, печать по центру корпуса чуть выше середины.
  tote: {
    type: 'tote',
    title: 'Шоппер',
    plural: 'Шоперы',
    sides: ['front'],
    colors: [],
    aspect: 363 / 723,
    base: { top: 0, height: 1 },
    zone: { x: 0.22, y: 0.56, width: 0.56, height: 0.3 },
  },

  // Кепка 499×498, печать на передней панели между люверсами и козырьком.
  cap: {
    type: 'cap',
    title: 'Кепка',
    plural: 'Кепки',
    sides: ['front'],
    colors: [],
    aspect: 499 / 498,
    base: { top: 0, height: 1 },
    zone: { x: 0.33, y: 0.34, width: 0.34, height: 0.16 },
  },
};

export const DEFAULT_GARMENT: GarmentType = 'tshirt';

/** Цвет, когда у товара список цветов пуст. */
export const DEFAULT_COLOR = 'black';

export function garmentOf(type: string | undefined): Garment {
  return GARMENTS[(type as GarmentType) ?? DEFAULT_GARMENT] ?? GARMENTS[DEFAULT_GARMENT];
}

/**
 * Адрес картинки изделия.
 *
 * У футболки свой файл на каждый цвет и сторону: перёд и спина сняты
 * отдельно, поэтому «сзади» больше не показывает вид спереди.
 * У шоппера и кепки цвет один и сторона одна — файл тоже один.
 */
export function garmentImage(garment: Garment, color: string, side: Side = 'front'): string {
  if (garment.colors.length === 0) return `/demo/${garment.type}.png`;

  // Цвет, которого нет среди снимков, подменяется существующим.
  //
  // Каталог приезжает из базы, и там легко оказаться цвету, для которого
  // снимка ещё не прислали: у ФОСИ так случилось с белым. Без подмены
  // витрина показывала битые картинки — худшее, что может увидеть
  // покупатель: вещь вроде есть, а выглядит как ошибка.
  //
  // Подменять честнее, чем прятать товар: цвет в палитре остаётся,
  // человек его выбирает, а изображение показываем ближайшее имеющееся.
  const known = garment.colors.includes(color) ? color : (garment.colors[0] as string);

  return `/demo/${garment.type}-${known}-${side}.png`;
}

/** Пропорции зоны печати: ширина к высоте в пикселях. */
export function zoneAspect(garment: Garment): number {
  return (garment.zone.width * garment.aspect) / garment.zone.height;
}

/**
 * Тёмная ли ткань.
 *
 * Нужно подписям принта: «EST.» и год обязаны читаться на изделии,
 * а не повторять цвет имени. В боте это отдельная мера `caption_color`,
 * и там же сказано почему — почти чёрные подписи на чёрной футболке
 * не видны вовсе.
 *
 * Список, а не расчёт яркости: цветов семь, они заданы вручную, и считать
 * контраст в рантайме ради семи известных значений — работа без пользы.
 * Контраст белого по ним: чёрная 7.1, тёмно-синяя 5.3, тёмно-серая 5.0,
 * графит 4.5 — при нужных 4.5.
 */
const DARK_COLORS = ['black', 'graphite', 'charcoal', 'navy'];

export function isDarkColor(color: string): boolean {
  return DARK_COLORS.includes(color);
}
